import { useState } from "react";

import { ArrowDownTrayIcon, EyeIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { LayersControl, MapContainer, Marker, Popup, TileLayer, WMSTileLayer } from "react-leaflet";
import { trackPromise } from "react-promise-tracker";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getStrapiClient } from "@/lib/strapiClient";
import type { Strapi } from "@/models/strapi";

import { mapLayers } from "../../components/map/layers";
import type { Accident } from "../../models/accident";
import type { Season } from "../../models/season";
import type { RootState } from "../../redux/store";

export default function AccidentsData() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const userState = useSelector((state: RootState) => state.user);

	const [temporada, setTemporada] = useState("");

	const seasonsQuery = useQuery({
		queryKey: ["temporadas", {}],
		queryFn: async () => {
			try {
				const client = getStrapiClient();
				const cast = <T,>(d: unknown) => d as unknown as T[];

				if (searchParams.get("season")) {
					setTemporada(searchParams.get("season") ?? "");
				} else {
					const configRes = await client.single("config").find({ populate: "*" });
					setTemporada((configRes as any).data?.temporadaActual?.name ?? "");
				}

				const seasonsRes = await client.collection("temporadas").find({
					sort: ["name:desc"],
				});
				return cast<Season>(seasonsRes.data);
			} catch (error) {
				console.error(error);
			}
		},
	});

	const [accidentsQuery, setAccidentsQuery] = useState<{
		sort: Record<string, string>;
		pagination: { page: number; pageSize: number };
	}>({
		sort: {
			date: "desc",
		},
		pagination: {
			page: 1,
			pageSize: 10,
		},
	});

	const mapPageSize = 100;

	const {
		data,
		isLoading: accidentsLoading,
		isFetching: accidentsFetching,
	} = useQuery({
		queryKey: ["accidents", { ...accidentsQuery, temporada: temporada }],
		queryFn: async () => {
			try {
				const client = getStrapiClient();
				const sort = Object.entries(accidentsQuery.sort).map(([key, value]) => `${key}:${value}`);
				const filters = temporada
					? {
						temporada: {
							name: {
								$eq: temporada,
							},
						},
					}
					: undefined;

				const response = await client.collection("accidents").find({
					filters,
					sort,
					populate: "*",
					pagination: accidentsQuery.pagination,
				});
				return response as Strapi<Accident[]>;
			} catch (error) {
				console.error(error);
			}
		},
	});

	const {
		data: mapData,
		isLoading: mapLoading,
		isFetching: mapFetching,
	} = useQuery({
		queryKey: ["accidents-map", { sort: accidentsQuery.sort, temporada }],
		enabled: !userState.loggedIn,
		queryFn: async () => {
			try {
				const client = getStrapiClient();
				const sort = Object.entries(accidentsQuery.sort).map(([key, value]) => `${key}:${value}`);
				const filters = temporada
					? {
						temporada: {
							name: {
								$eq: temporada,
							},
						},
					}
					: undefined;

				const firstResponse = (await client.collection("accidents").find({
					filters,
					sort,
					populate: "*",
					pagination: { page: 1, pageSize: mapPageSize },
				})) as Strapi<Accident[]>;

				const totalPages = firstResponse.meta.pagination.pageCount ?? 1;
				if (totalPages <= 1) {
					return firstResponse;
				}

				const remainingPages = Array.from({ length: totalPages - 1 }, (_, idx) => idx + 2);
				const remainingResponses = await Promise.all(
					remainingPages.map(async (page) => {
						const response = await client.collection("accidents").find({
							filters,
							sort,
							populate: "*",
							pagination: { page, pageSize: mapPageSize },
						});
						return response as Strapi<Accident[]>;
					}),
				);

				const combinedData = [
					...firstResponse.data,
					...remainingResponses.flatMap((response) => response.data),
				];

				return { ...firstResponse, data: combinedData };
			} catch (error) {
				console.error(error);
			}
		},
	});

	const Skeleton = ({ className }: { className?: string }) => (
		<div className={clsx("animate-pulse rounded-md bg-gray-200", className)} />
	);

	const isTableLoading = accidentsLoading || accidentsFetching;
	const isMapLoading = mapLoading || mapFetching;
	const totalPages = data?.meta.pagination.pageCount ?? 1;
	const currentPage = data?.meta.pagination.page ?? accidentsQuery.pagination.page;
	const totalItems = data?.meta.pagination.total ?? 0;

	const setPage = (page: number) =>
		setAccidentsQuery((prev) => ({
			...prev,
			pagination: { ...prev.pagination, page: Math.max(1, Math.min(totalPages, page)) },
		}));

	const setPageSize = (size: number) =>
		setAccidentsQuery((prev) => ({
			...prev,
			pagination: { page: 1, pageSize: size },
		}));

	const toggleSort = (field: string) => {
		setAccidentsQuery((prev) => {
			const current = prev.sort[field];
			const next = current === "asc" ? "desc" : "asc";
			return { ...prev, sort: { [field]: next } };
		});
	};

	const rows = (data?.data ?? []).map((accident: Accident) => ({
		id: accident.documentId,
		date: accident.date ? moment(accident.date).format("DD/MM/YYYY") : "",
		place: accident.place ? accident.place : "",
		danger_degree: accident.danger_degree
			? i18n.language === "es"
				? (accident.danger_degree.name_es ?? accident.danger_degree.name)
				: accident.danger_degree.name
			: t("accidents.models.unknown"),
		desencadenant: accident.desencadenant
			? `${i18n.language === "es" ? (accident.desencadenant.name_es ?? accident.desencadenant.name) : accident.desencadenant.name} (${accident.desencadenant.code})`
			: t("accidents.models.unknown"),
		origen: accident.origen
			? i18n.language === "es"
				? (accident.origen.name_es ?? accident.origen.name)
				: accident.origen.name
			: t("accidents.models.unknown"),
		size: accident.size ? accident.size : t("accidents.models.unknown"),
		members:
			accident.members !== null
				? accident.members !== -1
					? accident.members
					: t("accidents.models.unknown")
				: t("accidents.models.unknown"),
		dragged:
			accident.dragged !== null
				? accident.dragged !== -1
					? accident.dragged
					: t("accidents.models.unknown")
				: t("accidents.models.unknown"),
		damaged:
			accident.damaged !== null
				? accident.damaged !== -1
					? accident.damaged
					: t("accidents.models.unknown")
				: t("accidents.models.unknown"),
		dead:
			accident.dead !== null
				? accident.dead !== -1
					? accident.dead
					: t("accidents.models.unknown")
				: t("accidents.models.unknown"),
		activitat: accident.activitat
			? i18n.language === "es"
				? (accident.activitat.name_es ?? accident.activitat.name)
				: accident.activitat.name
			: t("accidents.models.unknown"),
	}));

	return (
		<div className="h-full w-full space-y-4 p-4 pb-20">
			{!userState.loggedIn && (
				<div className="z-0 h-full max-h-96 w-full rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
					{isMapLoading ? (
						<div className="flex h-full w-full flex-col justify-between space-y-3">
							<Skeleton className="h-6 w-32" />
							<Skeleton className="h-full w-full" />
						</div>
					) : (
						<MapContainer
							center={[43.003069514915786, -0.9930763932215235]}
							zoom={7}
							scrollWheelZoom={false}
							className="h-full w-full"
						>
							<LayersControl position="topright">
								{mapLayers.map((layer) => {
									switch (layer.type) {
										case "wms":
											return (
												<LayersControl.BaseLayer checked={layer.checked} name={layer.name} key={layer.name}>
													<WMSTileLayer
														url={layer.url}
														layers={layer.layer}
														format={layer.format}
														attribution={layer.attribution}
													/>
												</LayersControl.BaseLayer>
											);
										case "tile":
											return (
												<LayersControl.BaseLayer checked={layer.checked} name={layer.name} key={layer.name}>
												<TileLayer attribution={layer.attribution} url={layer.url} />
											</LayersControl.BaseLayer>
										);
										default:
											return <>Error</>;
									}
								})}
							</LayersControl>
							{mapData?.data.map((accident) => (
								<Marker
									key={accident.id}
									position={[accident.coordinates.latitude, accident.coordinates.longitude]}
									eventHandlers={{
										click: () => {
											navigate(`/accidents/${accident.documentId}`);
										},
										mouseover: (e) => {
											e.target.openPopup();
										},
									}}
								>
									<Popup>
										<dl className="flex flex-col">
											<div className="">
												<div className="font-semibold text-lg">{accident.place}</div>
												<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.date")}</dt>
												<dd className="mt-1 text-gray-900 text-sm">{accident.date}</dd>
											</div>
											{accident.scope && (
												<div className="">
													<dt className="font-medium text-gray-500 text-sm">{t("accidents.models.scope")}</dt>
													<dd className="mt-1 text-gray-900 text-sm">{accident.scope.name}</dd>
												</div>
											)}
											<div className="h-auto w-44 rounded-lg">
												{accident.gallery && accident.gallery.length > 0 && (
													<img
														src={accident.gallery[0].url}
														alt={accident.gallery[0].name}
														className="rounded-lg object-cover"
													/>
												)}
											</div>
											<div className="mt-2">
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														navigate(`/accidents/${accident.documentId}`);
													}}
													className="rounded-md bg-blue-600 px-2.5 py-1.5 font-semibold text-sm text-white shadow-xs hover:bg-blue-500 hover:text-white focus-visible:outline-2 focus-visible:outline-blue-600 focus-visible:outline-solid focus-visible:outline-offset-2"
												>
													{t("accidents.see_details")}
												</button>
											</div>
										</dl>
									</Popup>
								</Marker>
							))}
						</MapContainer>
					)}
				</div>
			)}
			<div>
				<div className="mb-2 flex w-1/4 items-center space-x-4">
					<select
						id="temporada"
						name="temporada"
						className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
						onChange={(e) => {
							setTemporada(e.target.value);
							setSearchParams({ season: e.target.value });
						}}
						value={temporada}
					>
						<option value="">{t("accidents.form.select.season")}</option>
						{seasonsQuery.isLoading &&
							Array.from({ length: 3 }).map((_, idx) => <option key={idx} value="">{t("loading")}</option>)}
						{seasonsQuery.data?.map((temp) => (
							<option key={temp.name} value={temp.name}>
								{temp.name}
							</option>
						))}
					</select>
					{userState.loggedIn && (
						<div>
							<button
								id="export_clients"
								type="button"
								className="inline-flex items-center rounded-md border border-transparent bg-blue-600 p-2 font-medium text-sm text-white shadow-xs hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
								onClick={() => {
									const fetchFile = async () => {
										const client = getStrapiClient();
										const response = await client.fetch(`getSeason?season=${temporada}`, {
											headers: { Accept: "text/csv" },
										});
										const blob = await response.blob();
										const href = URL.createObjectURL(blob);

										const link = document.createElement("a");
										link.href = href;
										link.setAttribute("download", `accidents_${temporada}.csv`);
										document.body.appendChild(link);
										link.click();

										// clean up "a" element & remove ObjectURL
										document.body.removeChild(link);
										URL.revokeObjectURL(href);
									};

									trackPromise(fetchFile());
								}}
							>
								<ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
							</button>
						</div>
					)}
				</div>
				<div>
					<div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
						{isTableLoading ? (
							<div className="space-y-2 p-4">
								{Array.from({ length: 8 }).map((_, idx) => (
									<div key={idx} className="grid grid-cols-10 gap-2">
										<Skeleton className="col-span-1 h-4" />
										<Skeleton className="col-span-2 h-4" />
										<Skeleton className="col-span-1 h-4" />
										<Skeleton className="col-span-2 h-4" />
										<Skeleton className="col-span-1 h-4" />
										<Skeleton className="col-span-1 h-4" />
										<Skeleton className="col-span-1 h-4" />
										<Skeleton className="col-span-1 h-4" />
									</div>
								))}
							</div>
						) : (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead onClick={() => toggleSort("date")} className="cursor-pointer">
											{t("accidents.models.date")}
										</TableHead>
										<TableHead onClick={() => toggleSort("place")} className="cursor-pointer">
											{t("accidents.models.place")}
										</TableHead>
										<TableHead>{t("accidents.models.danger_degree")}</TableHead>
										<TableHead>{t("accidents.models.desencadenant")}</TableHead>
										<TableHead>{t("accidents.models.origen")}</TableHead>
										<TableHead>{t("accidents.models.size")}</TableHead>
										<TableHead>{t("accidents.models.members")}</TableHead>
										<TableHead>{t("accidents.models.dragged")}</TableHead>
										<TableHead>{t("accidents.models.damaged")}</TableHead>
										<TableHead>{t("accidents.models.dead")}</TableHead>
										<TableHead>{t("accidents.models.activitat")}</TableHead>
										<TableHead>{t("accidents.models.actions")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{rows.map((row) => (
										<TableRow key={row.id}>
											<TableCell>{row.date}</TableCell>
											<TableCell>{row.place}</TableCell>
											<TableCell>{row.danger_degree}</TableCell>
											<TableCell>{row.desencadenant}</TableCell>
											<TableCell>{row.origen}</TableCell>
											<TableCell>{row.size}</TableCell>
											<TableCell>{row.members}</TableCell>
											<TableCell>{row.dragged}</TableCell>
											<TableCell>{row.damaged}</TableCell>
											<TableCell>{row.dead}</TableCell>
											<TableCell>{row.activitat}</TableCell>
											<TableCell>
												<button
													type="button"
													onClick={() => navigate(`/accidents/${row.id}`)}
													className="inline-flex items-center rounded-md border border-gray-200 px-2 py-1 font-medium text-blue-600 text-xs hover:bg-blue-50"
												>
													<EyeIcon className="mr-1 h-4 w-4" />
													{t("accidents.models.view", { defaultValue: "View" })}
												</button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
								<TableCaption className="mt-0 border-t border-gray-200 bg-gray-50/80 px-4 py-3 text-gray-600">
									<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
										<div className="flex flex-wrap items-center gap-3 text-sm">
											<div className="rounded-full bg-white px-3 py-1 text-gray-700 shadow-xs ring-1 ring-gray-200">
												{t("accidents.models.total", { defaultValue: "Total" })}: {totalItems}
											</div>
											<span className="text-gray-600">
												{t("accidents.models.page", { defaultValue: "Page" })} {currentPage}{" "}
												{t("accidents.models.of", { defaultValue: "of" })} {totalPages}
											</span>
										</div>
										<div className="flex flex-wrap items-center gap-3 text-sm">
											<label className="flex items-center gap-2 text-gray-600">
												<span>{t("accidents.models.rows_per_page", { defaultValue: "Rows per page" })}</span>
												<select
													className="h-8 min-h-8 rounded-md border border-gray-200 bg-white pl-2 pr-8 text-sm leading-none shadow-xs focus:border-blue-500 focus:outline-hidden"
													value={accidentsQuery.pagination.pageSize}
													onChange={(e) => setPageSize(Number(e.target.value))}
												>
													<option value={10}>10</option>
													<option value={25}>25</option>
													<option value={50}>50</option>
												</select>
											</label>
											<div className="flex items-center rounded-md border border-gray-200 bg-white p-1 shadow-xs">
												<Button
													variant="outline"
													size="sm"
													disabled={currentPage === 1}
													onClick={() => setPage(1)}
													className="h-8 w-8 border-0 p-0"
													aria-label="First page"
												>
													<ChevronsLeft className="h-4 w-4" />
												</Button>
												<Button
													variant="outline"
													size="sm"
													disabled={currentPage === 1}
													onClick={() => setPage(currentPage - 1)}
													className="h-8 w-8 border-0 p-0"
													aria-label="Previous page"
												>
													<ChevronLeft className="h-4 w-4" />
												</Button>
												<Button
													variant="outline"
													size="sm"
													disabled={currentPage >= totalPages}
													onClick={() => setPage(currentPage + 1)}
													className="h-8 w-8 border-0 p-0"
													aria-label="Next page"
												>
													<ChevronRight className="h-4 w-4" />
												</Button>
												<Button
													variant="outline"
													size="sm"
													disabled={currentPage >= totalPages}
													onClick={() => setPage(totalPages)}
													className="h-8 w-8 border-0 p-0"
													aria-label="Last page"
												>
													<ChevronsRight className="h-4 w-4" />
												</Button>
											</div>
										</div>
									</div>
								</TableCaption>
							</Table>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
